<template>
  <div>
    <!-- Sticky Header: 标题 + 步骤指示器 -->
    <div class="sticky top-0 z-30 bg-[#F8FAFC]">
      <!-- 标题区 -->
      <div class="flex flex-col items-center justify-center pt-8 pb-4">
        <div class="w-full max-w-3xl space-y-4">
          <div class="text-center space-y-2">
            <h1 class="text-3xl font-black text-slate-900 tracking-tight">
              <span class="text-[#F2C94C]">AI</span> 诊断你的管理困境
            </h1>
            <p class="text-slate-500 text-base max-w-xl mx-auto">
              描述您面临的挑战，并且回答几个针对性问题，AI将为您生成个性化行动建议。
            </p>
          </div>
        </div>
      </div>

      <!-- 步骤指示器 -->
      <div class="flex items-center justify-center gap-2 md:gap-4 py-4 mb-6">
        <template v-for="(s, idx) in steps">
          <div
            :key="s.step"
            :class="[
              'flex items-center gap-2 px-3 py-2 rounded-xl border transition-all',
              s.step === currentStep
                ? 'bg-[#F2C94C] border-[#F2C94C] text-white shadow-md'
                : s.step < currentStep
                  ? 'bg-[#F2C94C]/10 border-[#F2C94C]/30 text-[#F2C94C]'
                  : 'bg-white border-slate-200 text-slate-400'
            ]"
          >
            <div
              :class="[
                'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold',
                s.step === currentStep
                  ? 'bg-white/20 text-white'
                  : s.step < currentStep
                    ? 'bg-[#F2C94C] text-white'
                    : 'bg-slate-100 text-slate-400'
              ]"
            >
              {{ s.step < currentStep ? '✓' : s.step }}
            </div>
            <div class="hidden md:block">
              <div
                :class="[
                  'text-sm font-bold',
                  s.step === currentStep
                    ? 'text-white'
                    : s.step < currentStep
                      ? 'text-[#F2C94C]'
                      : 'text-slate-600'
                ]"
              >
                {{ s.title }}
              </div>
              <div
                :class="[
                  'text-[10px]',
                  s.step === currentStep ? 'text-white/80' : 'text-slate-400'
                ]"
              >
                {{ s.desc }}
              </div>
            </div>
          </div>
          <div
            v-if="idx < 2"
            :key="'arrow-' + s.step"
            :class="[
              'hidden md:block',
              s.step < currentStep ? 'text-[#F2C94C]' : 'text-slate-300'
            ]"
          >
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </template>
      </div>

      <!-- 步骤2的进度信息 -->
      <div v-if="currentStep === 2" class="max-w-3xl mx-auto pb-4 space-y-4">
        <div class="flex items-center gap-2">
          <span class="px-2.5 py-1 bg-[#F2C94C]/10 text-[#F2C94C] text-xs font-bold rounded-lg">步骤 2 / 3</span>
          <span class="text-sm font-bold text-slate-900">回答调研题目</span>
          <span class="text-xs text-slate-400">基于您的场景「{{ scenario.length > 20 ? scenario.slice(0, 20) + '...' : scenario }}」匹配题目</span>
        </div>

        <div class="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-bold text-slate-900">答题进度</span>
            <span class="text-sm font-bold text-[#F2C94C]">{{ completedCount }}/{{ dimensions.length }}</span>
          </div>
          <div class="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div
              class="h-full bg-[#F2C94C] transition-all duration-500"
              :style="{ width: progress + '%' }"
            />
          </div>
        </div>
      </div>
    </div><!-- /sticky header -->

    <!-- 步骤内容 -->
    <div class="max-w-3xl mx-auto">
      <StepScenario
        v-if="currentStep === 1"
        :scenario="scenario"
        :can-go-next="canGoToStep2"
        @scenario-change="setScenario"
        @next="handleNext"
      />

      <StepQuestions
        v-if="currentStep === 2"
        :scenario="scenario"
        :answers="answers"
        :details="details"
        :can-submit="canStartDiagnosis"
        @answers-change="setAnswers"
        @details-change="setDetails"
        @back="handleBack"
        @submit="handleSubmit"
      />

      <StepResult
        v-if="currentStep === 3"
        :scenario="scenario"
        @restart="handleRestart"
      />
    </div>
  </div>
</template>

<script>
import StepScenario from './wizard/StepScenario.vue';
import StepQuestions from './wizard/StepQuestions.vue';
import StepResult from './wizard/StepResult.vue';
import { DIMENSIONS_MAP, getQuestionSetKey } from './wizard/questionsData';

export default {
  name: 'DiagnoseWizard',
  components: {
    StepScenario,
    StepQuestions,
    StepResult
  },
  props: {
    onComplete: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      currentStep: 1,
      scenario: '',
      answers: {},
      details: '',
      steps: [
        { step: 1, title: '描述场景', desc: '输入你的管理困境' },
        { step: 2, title: '回答题目', desc: '几道关键调研题目' },
        { step: 3, title: 'AI 生成', desc: '定制化，可追问' }
      ]
    };
  },
  computed: {
    canGoToStep2() {
      return this.scenario.trim().length > 0;
    },
    canStartDiagnosis() {
      return Object.keys(this.answers).length >= 3;
    },
    questionSetKey() {
      return getQuestionSetKey(this.scenario);
    },
    dimensions() {
      return DIMENSIONS_MAP[this.questionSetKey] || [];
    },
    completedCount() {
      return Object.keys(this.answers).length;
    },
    progress() {
      return (this.completedCount / this.dimensions.length) * 100;
    }
  },
  methods: {
    setScenario(value) {
      this.scenario = value;
    },
    setAnswers(value) {
      this.answers = value;
    },
    setDetails(value) {
      this.details = value;
    },
    handleNext() {
      if (this.canGoToStep2) {
        this.currentStep = 2;
      }
    },
    handleBack() {
      this.currentStep = 1;
    },
    handleSubmit() {
      if (!this.canStartDiagnosis) return;
      this.currentStep = 3;
      const data = { scenario: this.scenario, answers: this.answers, details: this.details };
      this.$emit('complete', data);
      if (this.onComplete) {
        this.onComplete(data);
      }
    },
    handleRestart() {
      this.currentStep = 1;
      this.scenario = '';
      this.answers = {};
      this.details = '';
    }
  }
};
</script>
